/**
 * ConditionEditor — Date/time condition picker for welcome scenes
 */

import type { SceneCondition } from '../types';

interface ConditionEditorProps {
  value: SceneCondition | null;
  onChange: (condition: SceneCondition | null) => void;
}

type ConditionType = 'none' | 'date' | 'date_range' | 'time_range' | 'weekday';

const WEEKDAY_LABELS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];

function getConditionType(condition: SceneCondition | null): ConditionType {
  if (!condition) return 'none';
  return condition.type;
}

export function ConditionEditor({ value, onChange }: ConditionEditorProps) {
  const conditionType = getConditionType(value);

  const handleTypeChange = (type: ConditionType) => {
    if (type === 'none') {
      onChange(null);
    } else if (type === 'date') {
      onChange({ type: 'date', month: 1, day: 1, calendar: 'gregorian' });
    } else if (type === 'date_range') {
      onChange({ type: 'date_range', start: [1, 1], end: [1, 31], calendar: 'gregorian' });
    } else if (type === 'time_range') {
      onChange({ type: 'time_range', start_hour: 6, end_hour: 12 });
    } else if (type === 'weekday') {
      onChange({ type: 'weekday', days: [1] });
    }
  };

  const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';
  const selectClass = 'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <div className="space-y-3">
      {/* Type Selector */}
      <div className="flex flex-wrap gap-2">
        {(['none', 'date', 'date_range', 'time_range', 'weekday'] as ConditionType[]).map((t) => {
          const labels: Record<ConditionType, string> = {
            none: '无条件',
            date: '固定日期',
            date_range: '日期范围',
            time_range: '时段',
            weekday: '星期',
          };
          return (
            <button
              key={t}
              type="button"
              onClick={() => handleTypeChange(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                conditionType === t
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {labels[t]}
            </button>
          );
        })}
      </div>

      {/* Condition Inputs */}
      {value?.type === 'date' && (
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">月</label>
            <input
              type="number"
              min={1}
              max={12}
              value={value.month}
              onChange={(e) => onChange({ ...value, month: parseInt(e.target.value) || 1 })}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">日</label>
            <input
              type="number"
              min={1}
              max={31}
              value={value.day}
              onChange={(e) => onChange({ ...value, day: parseInt(e.target.value) || 1 })}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">历法</label>
            <select
              value={value.calendar}
              onChange={(e) => onChange({ ...value, calendar: e.target.value as 'gregorian' | 'lunar' })}
              className={selectClass + ' w-full'}
            >
              <option value="gregorian">公历</option>
              <option value="lunar">农历</option>
            </select>
          </div>
        </div>
      )}

      {value?.type === 'date_range' && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3">
              <label className="block text-xs text-gray-500 mb-1">历法</label>
              <select
                value={value.calendar}
                onChange={(e) => onChange({ ...value, calendar: e.target.value as 'gregorian' | 'lunar' })}
                className={selectClass}
              >
                <option value="gregorian">公历</option>
                <option value="lunar">农历</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">开始 (月/日)</label>
              <div className="flex gap-1">
                <input
                  type="number"
                  min={1}
                  max={12}
                  placeholder="月"
                  value={value.start[0]}
                  onChange={(e) => onChange({ ...value, start: [parseInt(e.target.value) || 1, value.start[1]] })}
                  className={inputClass}
                />
                <input
                  type="number"
                  min={1}
                  max={31}
                  placeholder="日"
                  value={value.start[1]}
                  onChange={(e) => onChange({ ...value, start: [value.start[0], parseInt(e.target.value) || 1] })}
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">结束 (月/日)</label>
              <div className="flex gap-1">
                <input
                  type="number"
                  min={1}
                  max={12}
                  placeholder="月"
                  value={value.end[0]}
                  onChange={(e) => onChange({ ...value, end: [parseInt(e.target.value) || 1, value.end[1]] })}
                  className={inputClass}
                />
                <input
                  type="number"
                  min={1}
                  max={31}
                  placeholder="日"
                  value={value.end[1]}
                  onChange={(e) => onChange({ ...value, end: [value.end[0], parseInt(e.target.value) || 1] })}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {value?.type === 'time_range' && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">开始小时 (0-23)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={value.start_hour}
              onChange={(e) => onChange({ ...value, start_hour: parseInt(e.target.value) || 0 })}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">结束小时 (0-23)</label>
            <input
              type="number"
              min={0}
              max={23}
              value={value.end_hour}
              onChange={(e) => onChange({ ...value, end_hour: parseInt(e.target.value) || 0 })}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {value?.type === 'weekday' && (
        <div>
          <label className="block text-xs text-gray-500 mb-2">选择星期 (可多选)</label>
          <div className="flex flex-wrap gap-2">
            {WEEKDAY_LABELS.map((label, idx) => {
              const isSelected = value.days.includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    const newDays = isSelected
                      ? value.days.filter((d) => d !== idx)
                      : [...value.days, idx].sort((a, b) => a - b);
                    onChange({ ...value, days: newDays.length > 0 ? newDays : [idx] });
                  }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    isSelected
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
